// Single source of truth for supported social platforms. Used by the tile
// editor's social dropdown, the "supported sites" list in Settings, link
// validation, and turning a bare handle into a canonical URL.
//
// detectPlatform(raw): guess a platform from a unique prefix ($ = Cash App) or
//   the domain of a pasted link. Ambiguous bare handles ("@x", "jasmine") return
//   null, so the UI falls back to the manual dropdown.
// buildSocialUrl(key, raw): given a chosen platform + a handle OR a full link,
//   return the canonical URL.
//
// Listed alphabetically by name; SUPPORTED_PLATFORM_NAMES derives from it, so the
// Settings list stays in order automatically.

const strip = (h) => h.trim().replace(/^@+/, '');

export const SOCIAL_PLATFORMS = [
  { key: 'audiomack',    name: 'Audiomack',    domains: ['audiomack.com'],    build: h => `https://audiomack.com/${strip(h)}` },
  { key: 'bandcamp',     name: 'Bandcamp',     domains: ['bandcamp.com'],     build: h => `https://${strip(h)}.bandcamp.com` },
  { key: 'behance',      name: 'Behance',      domains: ['behance.net'],      build: h => `https://behance.net/${strip(h)}` },
  { key: 'bereal',       name: 'BeReal',       domains: ['bereal.com'],       build: h => `https://bereal.com/${strip(h)}` },
  { key: 'bluesky',      name: 'Bluesky',      domains: ['bsky.app', 'bluesky.social'], build: h => `https://bsky.app/profile/${strip(h)}` },
  { key: 'cashapp',      name: 'Cash App',     domains: ['cash.app'],         prefix: '$', build: h => `https://cash.app/$${strip(h).replace(/^\$/, '')}` },
  { key: 'deviantart',   name: 'DeviantArt',   domains: ['deviantart.com'],   build: h => `https://deviantart.com/${strip(h)}` },
  { key: 'dribbble',     name: 'Dribbble',     domains: ['dribbble.com'],     build: h => `https://dribbble.com/${strip(h)}` },
  { key: 'facebook',     name: 'Facebook',     domains: ['facebook.com'],     build: h => `https://facebook.com/${strip(h)}` },
  { key: 'github',       name: 'GitHub',       domains: ['github.com'],       build: h => `https://github.com/${strip(h)}` },
  { key: 'instagram',    name: 'Instagram',    domains: ['instagram.com'],    build: h => `https://instagram.com/${strip(h)}` },
  { key: 'kick',         name: 'Kick',         domains: ['kick.com'],         build: h => `https://kick.com/${strip(h)}` },
  { key: 'linkedin',     name: 'LinkedIn',     domains: ['linkedin.com'],     build: h => `https://linkedin.com/in/${strip(h)}` },
  { key: 'livejournal',  name: 'LiveJournal',  domains: ['livejournal.com'],  build: h => `https://${strip(h)}.livejournal.com` },
  { key: 'medium',       name: 'Medium',       domains: ['medium.com'],       build: h => `https://medium.com/@${strip(h)}` },
  { key: 'myspace',      name: 'MySpace',      domains: ['myspace.com'],      build: h => `https://myspace.com/${strip(h)}` },
  { key: 'newgrounds',   name: 'Newgrounds',   domains: ['newgrounds.com'],   build: h => `https://${strip(h)}.newgrounds.com` },
  { key: 'patreon',      name: 'Patreon',      domains: ['patreon.com'],      build: h => `https://patreon.com/${strip(h)}` },
  { key: 'paypal',       name: 'PayPal',       domains: ['paypal.me', 'paypal.com'], build: h => `https://paypal.me/${strip(h)}` },
  { key: 'reverbnation', name: 'ReverbNation', domains: ['reverbnation.com'], build: h => `https://reverbnation.com/${strip(h)}` },
  { key: 'snapchat',     name: 'Snapchat',     domains: ['snapchat.com'],     build: h => `https://snapchat.com/add/${strip(h)}` },
  { key: 'soundcloud',   name: 'SoundCloud',   domains: ['soundcloud.com'],   build: h => `https://soundcloud.com/${strip(h)}` },
  { key: 'spotify',      name: 'Spotify',      domains: ['spotify.com'],      build: h => `https://open.spotify.com/user/${strip(h)}` },
  { key: 'substack',     name: 'Substack',     domains: ['substack.com'],     build: h => `https://${strip(h)}.substack.com` },
  { key: 'teepublic',    name: 'TeePublic',    domains: ['teepublic.com'],    build: h => `https://teepublic.com/user/${strip(h)}` },
  { key: 'threads',      name: 'Threads',      domains: ['threads.net', 'threads.com'], build: h => `https://threads.net/@${strip(h)}` },
  { key: 'tidal',        name: 'Tidal',        domains: ['tidal.com'],        build: h => `https://tidal.com/${strip(h)}` },
  { key: 'tiktok',       name: 'TikTok',       domains: ['tiktok.com'],       build: h => `https://tiktok.com/@${strip(h)}` },
  { key: 'truthsocial',  name: 'Truth Social', domains: ['truthsocial.com'],  build: h => `https://truthsocial.com/@${strip(h)}` },
  { key: 'tumblr',       name: 'Tumblr',       domains: ['tumblr.com'],       build: h => `https://${strip(h)}.tumblr.com` },
  { key: 'twitch',       name: 'Twitch',       domains: ['twitch.tv'],        build: h => `https://twitch.tv/${strip(h)}` },
  { key: 'venmo',        name: 'Venmo',        domains: ['venmo.com'],        build: h => `https://venmo.com/u/${strip(h)}` },
  { key: 'x',            name: 'X / Twitter',  domains: ['x.com', 'twitter.com'], build: h => `https://x.com/${strip(h)}` },
  { key: 'youtube',      name: 'YouTube',      domains: ['youtube.com', 'youtu.be'], build: h => `https://youtube.com/@${strip(h)}` },
];

export const SUPPORTED_PLATFORM_NAMES = SOCIAL_PLATFORMS.map(p => p.name);
export const PLATFORM_DOMAINS = SOCIAL_PLATFORMS.flatMap(p => p.domains);

function hostOf(raw) {
  try { return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname.replace(/^www\./, '').toLowerCase(); }
  catch { return null; }
}

// Guess platform from a unique prefix or a pasted link's domain. null = ambiguous.
export function detectPlatform(raw) {
  const v = (raw || '').trim();
  if (!v) return null;
  if (v.startsWith('$')) return 'cashapp';
  if (v.startsWith('@')) return null; // @handle is ambiguous across platforms
  const host = hostOf(v);
  if (!host) return null;
  const match = SOCIAL_PLATFORMS.find(p => p.domains.some(d => host === d || host.endsWith('.' + d)));
  return match ? match.key : null;
}

// Display label for a social URL: match the host against the registry and return
// the platform's real name (open.spotify.com -> "Spotify", cash.app -> "Cash App").
// Adds are validated against the same registry, so a non-whitelisted URL shouldn't
// exist; if one somehow does, we show a neutral "Link" rather than inventing a name.
export function platformLabel(raw) {
  const host = hostOf(raw);
  if (host) {
    const match = SOCIAL_PLATFORMS.find(p => p.domains.some(d => host === d || host.endsWith('.' + d)));
    if (match) return match.name;
  }
  return 'Link';
}

// Build the canonical URL for a chosen platform from a handle or a full link.
export function buildSocialUrl(key, raw) {
  const p = SOCIAL_PLATFORMS.find(x => x.key === key);
  if (!p) return null;
  const v = (raw || '').trim();
  if (!v) return null;
  // If they pasted a real link to this platform, keep it (normalize scheme only).
  const host = hostOf(v);
  if (host && p.domains.some(d => host === d || host.endsWith('.' + d))) {
    return v.startsWith('http') ? v : `https://${v}`;
  }
  // Otherwise treat it as a handle.
  return p.build(v);
}
